/**
 * Upload 前置判定 + Result 执行编排 + AI + Unlock + Upgrade 场景 Mock
 * 覆盖任务：T-U1/T-U3/T-U4/T-U10/T-U11/T-R1/T-R4/T-R5/T-R6/T-R7/T-R8/T-R9/T-R10/T-R11/T-R12
 */

import { generateId, delay, getMockDelay } from '../utils';
import {
    standardSubjectReady,
    subjectAiCompleted,
    subjectAiFailedRetryable,
    subjectWithUpgradeOffer,
    subjectReentryAwaitingTone,
    cloneSubjectPayload,
    applyCapabilityUnlockToSubject,
    stringifyCapabilitySetKey
} from '../fixtures/subjects';
import { findHistoryItemBySubjectId } from '../fixtures/history';

// ResultReentryContext 缓存 key（与 requestRegistry.js 保持一致）
const RESULT_REENTRY_CONTEXT_SESSION_KEY = 'analysis_result_reentry_context_v1';
const UPLOAD_SCENARIO_STORAGE_KEY = 'analysis_mock_upload_default_analysis_state_v1';

const POLICY_VERSION = '2026-04-05.r10.tier-free.v1';
const POLICY_ETAG = 'W/"analysis-policy-9e7b4f"';
const DEFAULT_RUN_CODE = 'upload_default_analysis';
const nowIso = () => new Date().toISOString();

const DEFAULT_CONTROL = Object.freeze({
    // quotes(operation=run) 分支：success | insufficient_credits | history_quota_exceeded | payment_pending_once | payment_pending_always
    quoteRunMode: 'success',
    // quotes(operation=capability_unlock) 分支：success | insufficient_credits | payment_pending_once | payment_pending_always
    quoteUnlockMode: 'success',
    // quotes(operation=ai_result_upgrade) 分支：success | insufficient_credits | payment_pending_once | payment_pending_always | paid_upgrade_not_available
    quoteUpgradeMode: 'success',

    // run-asset-resolutions 分支：standard | original_upload_required | processed_upload_required | source_not_ready | processed_mismatch
    assetResolutionMode: 'standard',

    // runs 分支：success | not_required | insufficient_credits | history_quota_exceeded | payment_pending_once | payment_pending_always | image_missing | processed_mismatch | rate_limited
    runMode: 'success',

    // ai-jobs 创建分支：success | success_missing_poll_job_id | ai_dispatch_not_required | ai_dispatch_input_conflict | delivery_retryable | manual_recovery_required | invalid_tone_context | idempotency_conflict | idempotency_conflict_missing_retry_id | roll_forward_recovery_success | ai_dispatch_input_conflict_full | delivery_init_failed_retryable
    aiJobCreateMode: 'success',
    // ai-jobs 最终结果分支：completed | failed_retryable | failed_input_conflict | failed_manual
    aiJobOutcomeMode: 'completed',

    // capability-unlocks 分支：success | insufficient_credits | payment_pending_once | payment_pending_always | confirm_mismatch | rate_limited
    capabilityUnlockMode: 'success',

    // ai-result-upgrades 分支：completed | awaiting_dispatch | paid_upgrade_not_available | insufficient_credits | payment_pending_once | payment_pending_always | rate_limited
    upgradeMode: 'completed',

    // 调整 mock 延迟倍率，1=默认
    delayMultiplier: 1
});

const normalizeString = (value) => {
    const normalized = String(value || '').trim();
    return normalized || null;
};

const toNumber = (value, fallback = 0) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : fallback;
};

const clone = (value) => JSON.parse(JSON.stringify(value));

const isBrowserSessionAvailable = () => typeof window !== 'undefined' && !!window.sessionStorage;

const readPersistedScenarioState = () => {
    if (!isBrowserSessionAvailable()) {
        return {
            createdSubjects: [],
            aiJobs: []
        };
    }

    try {
        const raw = window.sessionStorage.getItem(UPLOAD_SCENARIO_STORAGE_KEY);
        if (!raw) {
            return {
                createdSubjects: [],
                aiJobs: []
            };
        }

        const parsed = JSON.parse(raw);
        return {
            createdSubjects: Array.isArray(parsed?.createdSubjects) ? parsed.createdSubjects : [],
            aiJobs: Array.isArray(parsed?.aiJobs) ? parsed.aiJobs : []
        };
    } catch (error) {
        console.warn('[Mock] failed to read upload scenario state', error);
        return {
            createdSubjects: [],
            aiJobs: []
        };
    }
};

const writePersistedScenarioState = (payload = {}) => {
    if (!isBrowserSessionAvailable()) {
        return;
    }

    try {
        window.sessionStorage.setItem(UPLOAD_SCENARIO_STORAGE_KEY, JSON.stringify(payload));
    } catch (error) {
        console.warn('[Mock] failed to persist upload scenario state', error);
    }
};

const clearPersistedScenarioState = () => {
    if (!isBrowserSessionAvailable()) {
        return;
    }

    try {
        window.sessionStorage.removeItem(UPLOAD_SCENARIO_STORAGE_KEY);
    } catch (error) {
        console.warn('[Mock] failed to clear upload scenario state', error);
    }
};

const waitMockDelay = async (base = 1) => {
    const normalizedBase = Number.isFinite(Number(base)) ? Number(base) : 1;
    const effectiveDelay = Math.max(0, getMockDelay() * normalizedBase);
    await delay(effectiveDelay);
};

const withPolicyContext = (payload = {}) => ({
    ...payload,
    entitlement_context: payload.entitlement_context || {
        tier: 'standard',
        policy_version: POLICY_VERSION,
        policy_etag: POLICY_ETAG
    }
});

const buildMockError = ({
    code,
    status,
    message,
    responseData = null
}) => {
    const error = new Error(message || code || 'mock_error');
    error.code = code || 'mock_error';
    error.status = Number(status) || 500;
    if (responseData) {
        error.responseData = responseData;
    }
    return error;
};

const buildTierMatrix = ({
    free = { availability: 'full', credits: 0, confirmation_required: false, default_in_run: false },
    standard = { availability: 'full', credits: 0, confirmation_required: false, default_in_run: false },
    pro = { availability: 'full', credits: 0, confirmation_required: false, default_in_run: false }
} = {}) => ({
    free: {
        ...free,
        visible_fields: free.visible_fields || '*'
    },
    standard: {
        ...standard,
        visible_fields: standard.visible_fields || '*'
    },
    pro: {
        ...pro,
        visible_fields: pro.visible_fields || '*'
    }
});

const MOCK_CAPABILITY_SNAPSHOT = Object.freeze({
    policy_version: POLICY_VERSION,
    policy_etag: POLICY_ETAG,
    run_code: DEFAULT_RUN_CODE,
    tiers: ['free', 'standard', 'pro'],
    tier_benefits: {
        free: ['result_guide.luminosity_histogram', 'result_guide.rgb_histogram', 'compare.luminance_rgb_mask'],
        standard: [
            'result_guide.luminosity_histogram',
            'result_guide.rgb_histogram',
            'result_guide.luminance_rgb_mask',
            'result_guide.gaps_text_description',
            'summary.ai_gaps_summary',
            'summary.retouch_workflow',
            'compare.luminance_rgb_mask',
            'result_task.gaps_card_overall',
            'atomic_capability.image_tab',
            'atomic_capability.tonal_exposure',
            'atomic_capability.color_grading',
            'atomic_capability.global_color_base'
        ],
        pro: [
            'result_guide.luminosity_histogram',
            'result_guide.rgb_histogram',
            'result_guide.luminance_rgb_mask',
            'result_guide.gaps_text_description',
            'summary.ai_gaps_summary',
            'summary.retouch_workflow',
            'result_guide.gaps_visual_panel',
            'compare.luminance_rgb_mask',
            'compare.finetune_slider',
            'compare.point_inspector',
            'result_task.gaps_card_overall',
            'atomic_capability.image_tab',
            'atomic_capability.tonal_exposure',
            'atomic_capability.color_grading',
            'atomic_capability.global_color_base',
            'atomic_capability.global_color_palette'
        ]
    },
    default_bundle_by_tier: {
        free: {
            capability_codes: ['result_guide.luminosity_histogram', 'result_guide.rgb_histogram', 'compare.luminance_rgb_mask'],
            total_credits_required: 3
        },
        standard: {
            capability_codes: [
                'result_guide.luminosity_histogram',
                'result_guide.rgb_histogram',
                'result_guide.luminance_rgb_mask',
                'result_guide.gaps_text_description',
                'summary.ai_gaps_summary',
                'summary.retouch_workflow',
                'compare.luminance_rgb_mask',
                'result_task.gaps_card_overall',
                'atomic_capability.image_tab',
                'atomic_capability.tonal_exposure',
                'atomic_capability.color_grading',
                'atomic_capability.global_color_base'
            ],
            total_credits_required: 12
        },
        pro: {
            capability_codes: [
                'result_guide.luminosity_histogram',
                'result_guide.rgb_histogram',
                'result_guide.luminance_rgb_mask',
                'result_guide.gaps_text_description',
                'summary.ai_gaps_summary',
                'summary.retouch_workflow',
                'result_guide.gaps_visual_panel',
                'compare.luminance_rgb_mask',
                'compare.finetune_slider',
                'compare.point_inspector',
                'result_task.gaps_card_overall',
                'atomic_capability.image_tab',
                'atomic_capability.tonal_exposure',
                'atomic_capability.color_grading',
                'atomic_capability.global_color_base',
                'atomic_capability.global_color_palette'
            ],
            total_credits_required: 16
        }
    },
    capabilities: [
        {
            capability_code: 'result_guide.luminosity_histogram',
            module_code: 'result_guide',
            submodule_code: 'luminosity_histogram',
            capability_delivery_kind: 'frontend_only',
            tier_matrix: buildTierMatrix()
        },
        {
            capability_code: 'result_guide.rgb_histogram',
            module_code: 'result_guide',
            submodule_code: 'rgb_histogram',
            capability_delivery_kind: 'frontend_only',
            tier_matrix: buildTierMatrix()
        },
        {
            capability_code: 'result_guide.luminance_rgb_mask',
            module_code: 'result_guide',
            submodule_code: 'luminance_rgb_mask',
            capability_delivery_kind: 'frontend_only',
            tier_matrix: buildTierMatrix()
        },
        {
            capability_code: 'result_guide.gaps_text_description',
            module_code: 'result_guide',
            submodule_code: 'gaps_text_description',
            capability_delivery_kind: 'frontend_only',
            tier_matrix: buildTierMatrix()
        },
        {
            capability_code: 'summary.ai_gaps_summary',
            module_code: 'summary',
            submodule_code: 'ai_gaps_summary',
            capability_delivery_kind: 'backend_ai',
            tier_matrix: buildTierMatrix({
                free: { availability: 'partial', credits: 5, confirmation_required: true, default_in_run: false },
                standard: { availability: 'full', credits: 5, confirmation_required: true, default_in_run: false },
                pro: { availability: 'full', credits: 0, confirmation_required: false, default_in_run: false }
            })
        },
        {
            capability_code: 'summary.retouch_workflow',
            module_code: 'summary',
            submodule_code: 'retouch_workflow',
            capability_delivery_kind: 'backend_ai',
            tier_matrix: buildTierMatrix({
                free: { availability: 'unavailable', credits: null, confirmation_required: false, default_in_run: false },
                standard: { availability: 'full', credits: 5, confirmation_required: true, default_in_run: false },
                pro: { availability: 'full', credits: 0, confirmation_required: false, default_in_run: false }
            })
        },
        {
            capability_code: 'result_guide.gaps_visual_panel',
            module_code: 'result_guide',
            submodule_code: 'gaps_visual_panel',
            capability_delivery_kind: 'frontend_only',
            tier_matrix: buildTierMatrix({
                free: { availability: 'full', credits: 1, confirmation_required: true, default_in_run: false },
                standard: { availability: 'full', credits: 1, confirmation_required: true, default_in_run: false },
                pro: { availability: 'full', credits: 0, confirmation_required: false, default_in_run: true }
            })
        },
        {
            capability_code: 'compare.luminance_rgb_mask',
            module_code: 'compare',
            submodule_code: 'luminance_rgb_mask',
            capability_delivery_kind: 'frontend_only',
            tier_matrix: buildTierMatrix()
        },
        {
            capability_code: 'compare.point_inspector',
            module_code: 'compare',
            submodule_code: 'point_inspector',
            capability_delivery_kind: 'frontend_only',
            tier_matrix: buildTierMatrix({
                free: { availability: 'partial', credits: 1, confirmation_required: true, default_in_run: false },
                standard: { availability: 'partial', credits: 1, confirmation_required: true, default_in_run: false },
                pro: { availability: 'full', credits: 0, confirmation_required: false, default_in_run: false }
            })
        },
        {
            capability_code: 'result_task.gaps_card_overall',
            module_code: 'result_task',
            submodule_code: 'gaps_card_overall',
            capability_delivery_kind: 'frontend_only',
            tier_matrix: buildTierMatrix({
                free: { availability: 'partial', credits: 1, confirmation_required: true, default_in_run: false },
                standard: { availability: 'partial', credits: 1, confirmation_required: true, default_in_run: false },
                pro: { availability: 'full', credits: 0, confirmation_required: false, default_in_run: false }
            })
        },
        {
            capability_code: 'atomic_capability.image_tab',
            module_code: 'atomic_capability',
            submodule_code: 'image_tab',
            capability_delivery_kind: 'frontend_only',
            tier_matrix: buildTierMatrix()
        },
        {
            capability_code: 'atomic_capability.tonal_exposure',
            module_code: 'atomic_capability',
            submodule_code: 'tonal_exposure',
            capability_delivery_kind: 'frontend_only',
            tier_matrix: buildTierMatrix()
        },
        {
            capability_code: 'atomic_capability.color_grading',
            module_code: 'atomic_capability',
            submodule_code: 'color_grading',
            capability_delivery_kind: 'frontend_only',
            tier_matrix: buildTierMatrix({
                free: { availability: 'partial', credits: 2, confirmation_required: true, default_in_run: false },
                standard: { availability: 'partial', credits: 2, confirmation_required: true, default_in_run: false },
                pro: { availability: 'full', credits: 0, confirmation_required: false, default_in_run: false }
            })
        },
        {
            capability_code: 'atomic_capability.global_color_base',
            module_code: 'atomic_capability',
            submodule_code: 'global_color_base',
            capability_delivery_kind: 'frontend_only',
            tier_matrix: buildTierMatrix()
        },
        {
            capability_code: 'atomic_capability.global_color_palette',
            module_code: 'atomic_capability',
            submodule_code: 'global_color_palette',
            capability_delivery_kind: 'frontend_only',
            tier_matrix: buildTierMatrix({
                free: { availability: 'partial', credits: 1, confirmation_required: true, default_in_run: false },
                standard: { availability: 'partial', credits: 1, confirmation_required: true, default_in_run: false },
                pro: { availability: 'full', credits: 0, confirmation_required: false, default_in_run: false }
            })
        }
    ],
    compiled_at: nowIso()
});

const REFERENCE_CATALOG_ITEMS = Object.freeze([
    { file: 'ref1.jpg', name: '参考图1', category: 'portrait' },
    { file: 'ref2.jpg', name: '参考图2', category: 'landscape' },
    { file: 'ref3.jpeg', name: '参考图3', category: 'street' },
    { file: 'ref4.png', name: '参考图4', category: 'portrait' },
    { file: 'ref5.png', name: '参考图5', category: 'landscape' },
    { file: 'ref6.webp', name: '参考图6', category: 'street' },
    { file: 'ref7.jpg', name: '参考图7', category: 'portrait' },
    { file: 'ref8.jpg', name: '参考图8', category: 'landscape' },
    { file: 'ref9.jpg', name: '参考图9', category: 'street' },
    { file: 'ref10.png', name: '参考图10', category: 'portrait' }
]);

const clearResultReentryContext = () => {
    if (typeof window === 'undefined' || !window.sessionStorage) {
        return;
    }

    try {
        window.sessionStorage.removeItem(RESULT_REENTRY_CONTEXT_SESSION_KEY);
    } catch (error) {
        console.warn('[Mock] failed to clear ResultReentryContext', error);
    }
};

const buildMockHttpResponse = (data, {
    status = 200,
    headers = {}
} = {}) => ({
    __mockHttpMeta: {
        status,
        headers
    },
    data
});

const isPaymentPendingMode = (mode) => mode === 'payment_pending_once' || mode === 'payment_pending_always';

const shouldTriggerPendingByMode = ({ mode, tracker, key }) => {
    if (!isPaymentPendingMode(mode)) {
        return false;
    }

    if (mode === 'payment_pending_always') {
        return true;
    }

    const count = tracker.get(key) || 0;
    tracker.set(key, count + 1);
    return count === 0;
};

const normalizeCapabilityCodeSet = (capabilityCodes = []) => {
    const seen = new Set();
    const result = [];

    capabilityCodes.forEach((code) => {
        const normalizedCode = normalizeString(code);
        if (!normalizedCode || seen.has(normalizedCode)) {
            return;
        }
        seen.add(normalizedCode);
        result.push(normalizedCode);
    });

    return result;
};

const extractCapabilityCode = (entry) => normalizeString(entry?.capability_code || entry?.capabilityCode || entry?.code);

const extractCapabilityCredits = (entry) => {
    const displayPrice = entry?.display_price || entry?.displayPrice;
    if (displayPrice && typeof displayPrice === 'object') {
        return toNumber(displayPrice.credits, 0);
    }

    return toNumber(entry?.unlock_requirements?.credits, 0);
};

const findUnlockableCapabilityEntry = (subject = {}, capabilityCode) => {
    const unlockable = Array.isArray(subject?.unlockable_capabilities)
        ? subject.unlockable_capabilities
        : [];
    return unlockable.find((entry) => extractCapabilityCode(entry) === capabilityCode) || null;
};

const subjectSupportsUpgrade = (subject = {}) => Boolean(subject?.upgrade_offer?.run_code);

const createReferenceCatalogItems = () => REFERENCE_CATALOG_ITEMS.map((item, index) => ({
    reference_code: `reference_${index + 1}`,
    display_name: item.name,
    sort_order: index + 1,
    status: 'active',
    category: item.category,
    is_featured: index === 0 || index === 3,
    preview_image_url: `/data/references/${item.file}`
}));

const createQuoteItemsForRun = () => [
    { capability_code: 'result_guide.luminosity_histogram', credits: 1 },
    { capability_code: 'result_guide.rgb_histogram', credits: 1 },
    { capability_code: 'result_guide.luminance_rgb_mask', credits: 1 },
    { capability_code: 'result_guide.gaps_text_description', credits: 2 },
    { capability_code: 'summary.ai_gaps_summary', credits: 1 },
    { capability_code: 'summary.retouch_workflow', credits: 1 },
    { capability_code: 'compare.luminance_rgb_mask', credits: 0 },
    { capability_code: 'result_task.gaps_card_overall', credits: 1 },
    { capability_code: 'atomic_capability.image_tab', credits: 1 },
    { capability_code: 'atomic_capability.tonal_exposure', credits: 1 },
    { capability_code: 'atomic_capability.color_grading', credits: 1 },
    { capability_code: 'atomic_capability.global_color_base', credits: 1 }
];

const createRunChargeItems = () => createQuoteItemsForRun().map((item) => ({
    capability_code: item.capability_code,
    credits: item.credits
}));

const createRefreshPaymentsStatusNextAction = ({ endpoint = '/payments/status/' } = {}) => ({
    type: 'refresh_payments_status',
    method: 'GET',
    endpoint
});

const createRefreshSubjectNextAction = ({ subjectId } = {}) => {
    const normalizedSubjectId = normalizeString(subjectId);
    return {
        type: 'refresh_subject',
        method: 'GET',
        endpoint: normalizedSubjectId ? `/analysis/subjects/${normalizedSubjectId}/` : '/analysis/subjects/',
        ...(normalizedSubjectId ? {
            subject_id: normalizedSubjectId,
            params: {
                subject_id: normalizedSubjectId
            }
        } : {})
    };
};

const createPollAiJobNextAction = ({ jobId, omitJobId = false } = {}) => {
    const normalizedJobId = normalizeString(jobId);
    return {
        type: 'poll_ai_job',
        method: 'GET',
        endpoint: normalizedJobId ? `/analysis/ai-jobs/${normalizedJobId}/` : '/analysis/ai-jobs/',
        ...(!omitJobId && normalizedJobId ? {
            job_id: normalizedJobId,
            params: {
                job_id: normalizedJobId
            }
        } : {})
    };
};

const createRetryCreateAiJobNextAction = ({ executionId, retryRequestId } = {}) => {
    const normalizedExecutionId = normalizeString(executionId);
    const normalizedRetryRequestId = normalizeString(retryRequestId);
    return {
        type: 'retry_create_ai_job',
        method: 'POST',
        endpoint: '/analysis/ai-jobs/',
        ...(normalizedExecutionId ? { execution_id: normalizedExecutionId } : {}),
        ...(normalizedRetryRequestId ? { retry_request_id: normalizedRetryRequestId } : {}),
        params: {
            ...(normalizedExecutionId ? { execution_id: normalizedExecutionId } : {}),
            ...(normalizedRetryRequestId ? { retry_request_id: normalizedRetryRequestId } : {})
        }
    };
};

const createRestartAnalysisFlowNextAction = ({ subjectId = null, executionId = null } = {}) => {
    const normalizedSubjectId = normalizeString(subjectId);
    const normalizedExecutionId = normalizeString(executionId);
    return {
        type: 'restart_analysis_flow',
        ...(normalizedSubjectId ? { subject_id: normalizedSubjectId } : {}),
        ...(normalizedExecutionId ? { execution_id: normalizedExecutionId } : {}),
        reason: '当前本地 AI 输入已不再属于原 execution 的同一语义'
    };
};

const createManualRecoveryNextAction = ({ subjectId = null, executionId = null } = {}) => {
    const normalizedSubjectId = normalizeString(subjectId);
    const normalizedExecutionId = normalizeString(executionId);
    return {
        type: 'manual_recovery',
        ...(normalizedSubjectId ? { subject_id: normalizedSubjectId } : {}),
        ...(normalizedExecutionId ? { execution_id: normalizedExecutionId } : {}),
        reason: '需要人工介入恢复'
    };
};

const buildPaymentMaterializationPendingExtra = () => ({
    payment_materialization_pending: true,
    next_action: createRefreshPaymentsStatusNextAction()
});

const buildRetryableAiJobFailure = ({ mode, retryRequestId, executionId = null, subjectId = null } = {}) => {
    if (mode === 'failed_input_conflict') {
        return {
            retryable: false,
            retry_with_same_request_id: false,
            next_action: createRestartAnalysisFlowNextAction({ subjectId, executionId })
        };
    }

    if (mode === 'failed_manual') {
        return {
            retryable: false,
            retry_with_same_request_id: false,
            next_action: createManualRecoveryNextAction({ subjectId, executionId })
        };
    }

    return {
        retryable: true,
        retry_with_same_request_id: true,
        retry_request_id: retryRequestId,
        next_action: {
            ...createRetryCreateAiJobNextAction({ executionId, retryRequestId }),
            reason: 'AI 服务暂时不可用，建议稍后重试'
        }
    };
};

const createAiMarkdown = () => `## 色彩分析报告

### 整体色调
主色调偏向暖色系（色温约5800K），以橙黄为主，辅以少量青蓝冷色点缀，形成温和对比。

### 曝光分析
- **高光**：略微过曝约0.3EV，建议降低高光恢复细节
- **中间调**：分布均匀，覆盖直方图中央40%区域，层次丰富
- **阴影**：保留约95%细节，未出现死黑，可安全提亮

### 色彩建议
1. 色温降低至5500K，增加清爽感
2. 阴影提亮+15，增强画面层次
3. 饱和度适当提升+8，突出主体肤色
`;

export const uploadAndExecuteScenario = {
    name: 'upload_default_analysis',
    description: '标准 upload 分析链路 + capability unlock + paid upgrade + ai-jobs 完整分支覆盖',

    state: {
        executedRuns: new Set(),
        createdSubjects: new Map(),
        aiJobs: new Map(),
        executionIndex: new Map(), // execution_id -> { subject_id, operation_type, run_code }
        requestRunIndex: new Map(), // request_id -> run response
        requestUnlockIndex: new Map(), // request_id -> unlock response
        requestUpgradeIndex: new Map(), // request_id -> upgrade response
        quotePendingTracker: new Map(),
        mutationPendingTracker: new Map(),
        aiReplayIndex: new Map(), // execution_id::request_id -> job_id
        pendingJobByExecution: new Map(),
        control: {
            ...DEFAULT_CONTROL
        }
    },

    _resolveControl() {
        return {
            ...DEFAULT_CONTROL,
            ...(this.state?.control || {})
        };
    },

    _delayMultiplier() {
        const delayMultiplier = toNumber(this._resolveControl().delayMultiplier, 1);
        return delayMultiplier > 0 ? delayMultiplier : 1;
    },

    _buildMutationPendingKey(operationType, requestId) {
        return `${operationType || 'unknown'}::${normalizeString(requestId) || 'missing_request_id'}`;
    },

    _persistRuntimeState() {
        writePersistedScenarioState({
            createdSubjects: Array.from(this.state.createdSubjects.entries()).map(([subjectId, subjectPayload]) => ([
                subjectId,
                cloneSubjectPayload(subjectPayload) || clone(subjectPayload)
            ])),
            aiJobs: Array.from(this.state.aiJobs.entries()).map(([jobId, jobPayload]) => ([
                jobId,
                clone(jobPayload)
            ]))
        });
    },

    _restorePersistedRuntimeState() {
        const persistedState = readPersistedScenarioState();

        this.state.createdSubjects.clear();
        this.state.aiJobs.clear();
        this.state.executionIndex.clear();
        this.state.pendingJobByExecution.clear();

        persistedState.createdSubjects.forEach((entry) => {
            const subjectId = normalizeString(entry?.[0]);
            const subjectPayload = entry?.[1];
            if (!subjectId || !subjectPayload) {
                return;
            }

            const clonedSubject = cloneSubjectPayload(subjectPayload) || clone(subjectPayload);
            this.state.createdSubjects.set(subjectId, clonedSubject);

            const pendingExecution = clonedSubject?.pending_ai_execution || null;
            const executionId = normalizeString(
                pendingExecution?.execution_id
                || clonedSubject?.viewer_state?.current_execution_id
            );
            if (!executionId) {
                return;
            }

            this.state.executionIndex.set(executionId, {
                subject_id: normalizeString(clonedSubject?.subject_id) || subjectId,
                operation_type: normalizeString(pendingExecution?.operation_type) || 'run',
                run_code: normalizeString(clonedSubject?.subject_context?.run_code) || DEFAULT_RUN_CODE
            });
        });

        persistedState.aiJobs.forEach((entry) => {
            const jobId = normalizeString(entry?.[0]);
            const jobPayload = entry?.[1];
            if (!jobId || !jobPayload) {
                return;
            }

            const clonedJob = clone(jobPayload);
            this.state.aiJobs.set(jobId, clonedJob);

            const executionId = normalizeString(clonedJob?.execution_id);
            const subjectId = normalizeString(clonedJob?.subject_id);
            if (executionId && subjectId && !this.state.executionIndex.has(executionId)) {
                this.state.executionIndex.set(executionId, {
                    subject_id: subjectId,
                    operation_type: normalizeString(clonedJob?.operation_type) || 'run',
                    run_code: DEFAULT_RUN_CODE
                });
            }

            if (executionId && (clonedJob?.status === 'queued' || clonedJob?.status === 'running')) {
                this.state.pendingJobByExecution.set(executionId, jobId);
            }
        });
    },

    _findSubjectInScenario(subjectId) {
        const normalizedSubjectId = normalizeString(subjectId);
        if (!normalizedSubjectId) {
            return null;
        }

        const localSubject = this.state.createdSubjects.get(normalizedSubjectId);
        if (localSubject) {
            return localSubject;
        }

        const historyItem = findHistoryItemBySubjectId(normalizedSubjectId);
        if (!historyItem) {
            return null;
        }

        const subject = historyItem.current_visible_result_status === 'ready'
            ? subjectWithUpgradeOffer({
                subjectId: historyItem.subject_id,
                originalHashId: historyItem.hash_id,
                processedHashId: `${historyItem.hash_id}_processed`,
                referenceCode: historyItem.reference_code || 'reference_1',
                runCode: DEFAULT_RUN_CODE,
                targetResultExists: false
            })
            : historyItem.current_visible_result_status === 'pending'
                ? subjectReentryAwaitingTone({
                    subjectId: historyItem.subject_id,
                    originalHashId: historyItem.hash_id,
                    processedHashId: `${historyItem.hash_id}_processed`,
                    referenceCode: historyItem.reference_code || 'reference_1',
                    runCode: DEFAULT_RUN_CODE
                })
                : standardSubjectReady({
                    subjectId: historyItem.subject_id,
                    originalHashId: historyItem.hash_id,
                    processedHashId: `${historyItem.hash_id}_processed`,
                    referenceCode: historyItem.reference_code || 'reference_1',
                    runCode: DEFAULT_RUN_CODE,
                    aiDispatchState: 'awaiting_dispatch'
                });

        this._setSubject(subject.subject_id, subject);
        const pendingExecutionId = normalizeString(subject?.pending_ai_execution?.execution_id);
        if (pendingExecutionId) {
            this.state.executionIndex.set(pendingExecutionId, {
                subject_id: subject.subject_id,
                operation_type: subject?.pending_ai_execution?.operation_type || 'run',
                run_code: subject?.subject_context?.run_code || DEFAULT_RUN_CODE
            });
        }

        return subject;
    },

    _setSubject(subjectId, subjectPayload) {
        const normalizedSubjectId = normalizeString(subjectId || subjectPayload?.subject_id);
        if (!normalizedSubjectId || !subjectPayload) {
            return;
        }
        const cloned = cloneSubjectPayload(subjectPayload) || clone(subjectPayload);
        this.state.createdSubjects.set(normalizedSubjectId, cloned);
        this._persistRuntimeState();
    },

    _getSubject(subjectId) {
        const subject = this._findSubjectInScenario(subjectId);
        return subject ? cloneSubjectPayload(subject) : null;
    },

    _bindExecution({ executionId, subjectId, operationType = 'run', runCode = DEFAULT_RUN_CODE }) {
        const normalizedExecutionId = normalizeString(executionId);
        const normalizedSubjectId = normalizeString(subjectId);
        if (!normalizedExecutionId || !normalizedSubjectId) {
            return;
        }

        this.state.executionIndex.set(normalizedExecutionId, {
            subject_id: normalizedSubjectId,
            operation_type: operationType,
            run_code: runCode
        });
    },

    _resolveExecutionBinding(executionId) {
        const normalizedExecutionId = normalizeString(executionId);
        if (!normalizedExecutionId) {
            return null;
        }

        const binding = this.state.executionIndex.get(normalizedExecutionId);
        if (!binding) {
            return null;
        }

        return {
            execution_id: normalizedExecutionId,
            subject_id: binding.subject_id,
            operation_type: binding.operation_type || 'run',
            run_code: binding.run_code || DEFAULT_RUN_CODE
        };
    },

    _buildRunQuoteResponse({ sufficientToContinue, sufficientCredits, reasonCode = null, paymentPending = false, nextAction = null }) {
        const quotedItems = createQuoteItemsForRun();
        return {
            operation_type: 'run',
            tier: 'standard',
            balance: {
                trial: sufficientCredits ? 5 : 0,
                cycle: sufficientCredits ? 100 : 3,
                total: sufficientCredits ? 105 : 3
            },
            quota_preview: {
                decision: sufficientToContinue ? 'consume_and_pass' : (reasonCode || 'blocked'),
                slot_delta: 1,
                history_limit: 100,
                history_used: reasonCode === 'history_quota_exceeded' ? 100 : 12,
                history_remaining_after_execute: reasonCode === 'history_quota_exceeded' ? 0 : 87,
                would_exceed_limit: reasonCode === 'history_quota_exceeded'
            },
            quoted_items: quotedItems,
            confirmation_required_capabilities: [],
            total_credits_required: quotedItems.reduce((acc, item) => acc + toNumber(item.credits, 0), 0),
            sufficient_credits: sufficientCredits,
            sufficient_to_continue: sufficientToContinue,
            payment_materialization_pending: paymentPending,
            next_action: nextAction,
            reason_code: reasonCode
        };
    },

    _buildUnlockQuoteResponse({ subjectId, capabilityCodes = [], sufficientToContinue, sufficientCredits, reasonCode = null, paymentPending = false, nextAction = null }) {
        const subject = this._findSubjectInScenario(subjectId);
        const normalizedCodes = normalizeCapabilityCodeSet(capabilityCodes);
        const quotedItems = normalizedCodes.map((capabilityCode) => {
            const unlockableEntry = findUnlockableCapabilityEntry(subject, capabilityCode);
            return {
                capability_code: capabilityCode,
                credits: extractCapabilityCredits(unlockableEntry)
            };
        });

        const confirmationRequiredCapabilities = normalizedCodes.filter((capabilityCode) => {
            const unlockableEntry = findUnlockableCapabilityEntry(subject, capabilityCode);
            return unlockableEntry?.confirmation_required !== false;
        });

        return {
            operation_type: 'capability_unlock',
            subject_id: subjectId,
            tier: 'standard',
            balance: {
                trial: sufficientCredits ? 5 : 0,
                cycle: sufficientCredits ? 100 : 2,
                total: sufficientCredits ? 105 : 2
            },
            quoted_items: quotedItems,
            confirmation_required_capabilities: confirmationRequiredCapabilities,
            total_credits_required: quotedItems.reduce((acc, item) => acc + toNumber(item.credits, 0), 0),
            sufficient_credits: sufficientCredits,
            sufficient_to_continue: sufficientToContinue,
            payment_materialization_pending: paymentPending,
            next_action: nextAction,
            reason_code: reasonCode
        };
    },

    _buildUpgradeQuoteResponse({ subjectId, runCode, sufficientToContinue, sufficientCredits, reasonCode = null, paymentPending = false, nextAction = null }) {
        const subject = this._findSubjectInScenario(subjectId);
        const upgradeCredits = toNumber(subject?.upgrade_offer?.display_price?.credits, 6);

        return {
            operation_type: 'ai_result_upgrade',
            subject_id: subjectId,
            run_code: runCode,
            tier: 'standard',
            balance: {
                trial: sufficientCredits ? 5 : 0,
                cycle: sufficientCredits ? 100 : 1,
                total: sufficientCredits ? 105 : 1
            },
            quoted_items: [
                {
                    capability_code: 'ai_result_upgrade',
                    credits: upgradeCredits
                }
            ],
            confirmation_required_capabilities: [],
            total_credits_required: upgradeCredits,
            sufficient_credits: sufficientCredits,
            sufficient_to_continue: sufficientToContinue,
            payment_materialization_pending: paymentPending,
            next_action: nextAction,
            reason_code: reasonCode
        };
    },

    _buildRunError(code, status, message, extra = {}) {
        throw buildMockError({
            code,
            status,
            message,
            responseData: {
                reason_code: code,
                ...extra
            }
        });
    },

    _resolveRunDispatchState(runMode) {
        if (runMode === 'not_required') {
            return 'not_required';
        }
        return 'awaiting_dispatch';
    },

    _handleAiJobFailureToSubject(binding, job) {
        const subject = this._findSubjectInScenario(binding.subject_id);
        if (!subject) {
            return;
        }

        const retryPayload = buildRetryableAiJobFailure({
            mode: job.outcome_mode,
            retryRequestId: job.retry_request_id,
            executionId: binding.execution_id,
            subjectId: binding.subject_id
        });

        if (job.outcome_mode === 'failed_retryable') {
            const failedSubject = subjectAiFailedRetryable({
                subjectId: subject.subject_id,
                executionId: binding.execution_id,
                requestId: job.request_id,
                retryRequestId: job.retry_request_id,
                operationType: binding.operation_type,
                originalHashId: subject?.subject_context?.original_hash_id,
                processedHashId: subject?.subject_context?.processed_image_hash,
                referenceCode: subject?.subject_context?.reference_code,
                runCode: binding.run_code
            });
            this._setSubject(subject.subject_id, {
                ...subject,
                ...failedSubject,
                current_visible_result: subject.current_visible_result || failedSubject.current_visible_result || null
            });
            return;
        }

        const nextSubject = cloneSubjectPayload(subject);
        nextSubject.ai_state = {
            ...(nextSubject.ai_state || {}),
            ai_dispatch_state: 'failed',
            dispatch_state: 'failed',
            last_dispatch_at: nowIso()
        };
        nextSubject.pending_ai_execution = {
            execution_id: binding.execution_id,
            operation_type: binding.operation_type,
            ai_dispatch_state: 'failed',
            target_generation: binding.operation_type === 'ai_result_upgrade'
                ? toNumber(nextSubject?.upgrade_offer?.target_generation, 2)
                : null,
            requestable: retryPayload.retryable === true,
            request_id: job.request_id,
            job_id: job.job_id,
            status: 'failed',
            retryable: retryPayload.retryable,
            retry_with_same_request_id: retryPayload.retry_with_same_request_id,
            retry_request_id: retryPayload.retry_request_id || null,
            next_action: retryPayload.next_action,
            ai_effective_source_kind: subject?.pending_ai_execution?.ai_effective_source_kind || 'processed',
            ai_effective_source_hash_id: subject?.pending_ai_execution?.ai_effective_source_hash_id || null,
            dispatch_payload: null
        };
        this._setSubject(nextSubject.subject_id, nextSubject);
    },

    _handleAiJobCompletionToSubject(binding, job) {
        const subject = this._findSubjectInScenario(binding.subject_id);
        if (!subject) {
            return;
        }

        let completedSubject = subjectAiCompleted({
            subjectId: subject.subject_id,
            executionId: binding.execution_id,
            requestId: job.request_id,
            operationType: binding.operation_type,
            originalHashId: subject?.subject_context?.original_hash_id,
            processedHashId: subject?.subject_context?.processed_image_hash,
            referenceCode: subject?.subject_context?.reference_code,
            runCode: binding.run_code,
            analysisMarkdown: createAiMarkdown(),
            aiEffectiveSourceKind: subject?.pending_ai_execution?.ai_effective_source_kind || 'processed',
            aiEffectiveSourceHashId: subject?.pending_ai_execution?.ai_effective_source_hash_id || subject?.subject_context?.processed_image_hash || null,
            upgradeGeneration: binding.operation_type === 'ai_result_upgrade'
                ? toNumber(subject?.upgrade_offer?.target_generation, 2)
                : toNumber(subject?.viewer_state?.current_unlocked_generation, 1)
        });

        if (binding.operation_type === 'ai_result_upgrade') {
            completedSubject = {
                ...completedSubject,
                viewer_state: {
                    ...(completedSubject.viewer_state || {}),
                    current_unlocked_generation: toNumber(subject?.upgrade_offer?.target_generation, 2)
                },
                upgrade_offer: null,
                result_update_notice: {
                    update_kind: 'paid_upgrade_applied',
                    effective_at: nowIso(),
                    previous_result_id: subject?.current_visible_result?.result_id || null,
                    current_result_id: completedSubject?.current_visible_result?.result_id || null
                }
            };
        }

        this._setSubject(completedSubject.subject_id, completedSubject);
    },

    _buildAiJobResponse(job) {
        return {
            execution_id: job.execution_id,
            subject_id: job.subject_id,
            job_id: job.job_id,
            status: job.status,
            ai_dispatch_state: job.status,
            progress_percent: job.progress_percent,
            retryable: job.retryable,
            retry_with_same_request_id: job.retry_with_same_request_id,
            retry_request_id: job.retry_request_id,
            next_action: job.next_action || null,
            analysis_markdown: job.status === 'completed' ? createAiMarkdown() : null,
            ai_result_id: job.status === 'completed' ? `ai_res_${job.job_id}` : null,
            meta: {
                provider: 'photone',
                model: 'gpt-5.4',
                prompt_version: '2026-04-05.r10',
                poll_after_seconds: job.status === 'running' ? 1 : 2,
                operation_type: job.operation_type
            }
        };
    },

    _advanceAiJob(job) {
        if (!job || job.status === 'completed' || job.status === 'failed') {
            return;
        }

        job.poll_count += 1;
        console.log('[Mock] ai-jobs _advanceAiJob:', { jobId: job.job_id, pollCount: job.poll_count, currentStatus: job.status });

        if (job.status === 'queued') {
            job.status = 'running';
            job.progress_percent = 45;
            job.next_action = createPollAiJobNextAction({
                jobId: job.job_id,
                omitJobId: job.missing_poll_job_id_in_next_action === true
            });
            console.log('[Mock] ai-jobs transitioned: queued -> running');
            return;
        }

        if (job.status === 'running' && job.poll_count < 3) {
            job.progress_percent = Math.min(95, job.progress_percent + 20);
            job.next_action = createPollAiJobNextAction({
                jobId: job.job_id,
                omitJobId: job.missing_poll_job_id_in_next_action === true
            });
            console.log('[Mock] ai-jobs progress updated:', job.progress_percent);
            return;
        }

        if (job.outcome_mode === 'completed') {
            job.status = 'completed';
            job.progress_percent = 100;
            job.retryable = false;
            job.retry_with_same_request_id = false;
            job.next_action = createRefreshSubjectNextAction({ subjectId: job.subject_id });
            console.log('[Mock] ai-jobs transitioned: running -> completed');
            return;
        }

        job.status = 'failed';
        const retryPayload = buildRetryableAiJobFailure({
            mode: job.outcome_mode,
            retryRequestId: job.retry_request_id,
            executionId: job.execution_id,
            subjectId: job.subject_id
        });
        job.retryable = retryPayload.retryable === true;
        job.retry_with_same_request_id = retryPayload.retry_with_same_request_id === true;
        job.next_action = retryPayload.next_action || null;
        job.progress_percent = 100;
        console.log('[Mock] ai-jobs transitioned: running -> failed, outcomeMode:', job.outcome_mode);
    },

    _resolveSubjectForUpgrade(subjectId) {
        const subject = this._findSubjectInScenario(subjectId);
        if (!subject) {
            this._buildRunError('subject_not_found', 404, 'subject_not_found');
        }

        if (!subjectSupportsUpgrade(subject)) {
            this._buildRunError('paid_upgrade_not_available', 409, 'paid_upgrade_not_available');
        }

        return subject;
    },

    _createUpgradeExecution({ subject, requestId, runCode, awaitingDispatch = false }) {
        const executionId = `exec_upg_${generateId().slice(0, 12)}`;
        const targetGeneration = toNumber(subject?.upgrade_offer?.target_generation, 2);

        const nextSubject = cloneSubjectPayload(subject);

        if (!awaitingDispatch) {
            const upgradedResult = subjectAiCompleted({
                subjectId: nextSubject.subject_id,
                executionId,
                requestId,
                operationType: 'ai_result_upgrade',
                originalHashId: nextSubject?.subject_context?.original_hash_id,
                processedHashId: nextSubject?.subject_context?.processed_image_hash,
                referenceCode: nextSubject?.subject_context?.reference_code,
                runCode,
                analysisMarkdown: createAiMarkdown(),
                upgradeGeneration: targetGeneration,
                aiEffectiveSourceKind: nextSubject?.pending_ai_execution?.ai_effective_source_kind || 'processed',
                aiEffectiveSourceHashId: nextSubject?.pending_ai_execution?.ai_effective_source_hash_id || nextSubject?.subject_context?.processed_image_hash || null
            });

            this._setSubject(nextSubject.subject_id, {
                ...upgradedResult,
                viewer_state: {
                    ...(upgradedResult.viewer_state || {}),
                    current_unlocked_generation: targetGeneration
                },
                upgrade_offer: null,
                result_update_notice: {
                    update_kind: 'paid_upgrade_applied',
                    effective_at: nowIso(),
                    previous_result_id: nextSubject?.current_visible_result?.result_id || null,
                    current_result_id: upgradedResult?.current_visible_result?.result_id || null
                }
            });

            this._bindExecution({
                executionId,
                subjectId: nextSubject.subject_id,
                operationType: 'ai_result_upgrade',
                runCode
            });

            return {
                execution_id: executionId,
                subject_id: nextSubject.subject_id,
                upgrade_state: 'completed',
                ai_dispatch_state: 'not_required',
                target_generation: targetGeneration,
                current_unlocked_generation_before: toNumber(subject?.viewer_state?.current_unlocked_generation, 1),
                current_visible_result_id: upgradedResult?.current_visible_result?.result_id || null
            };
        }

        nextSubject.pending_ai_execution = {
            execution_id: executionId,
            operation_type: 'ai_result_upgrade',
            ai_dispatch_state: 'awaiting_dispatch',
            target_generation: targetGeneration,
            requestable: true,
            job_id: null,
            status: null,
            request_id: requestId,
            retryable: false,
            retry_with_same_request_id: false,
            retry_request_id: null,
            ai_effective_source_kind: nextSubject?.current_visible_result?.ai_effective_source_kind || 'processed',
            ai_effective_source_hash_id: nextSubject?.current_visible_result?.ai_effective_source_hash_id || nextSubject?.subject_context?.processed_image_hash || null,
            dispatch_payload: {
                execution_id: executionId,
                request_id: requestId,
                tone_color_adjustment_context: {
                    schema_version: 'tone-context-schema.v0.1'
                }
            },
            next_action: null
        };
        nextSubject.ai_state = {
            ...(nextSubject.ai_state || {}),
            ai_dispatch_state: 'awaiting_dispatch',
            dispatch_state: 'awaiting_dispatch'
        };

        this._setSubject(nextSubject.subject_id, nextSubject);
        this._bindExecution({
            executionId,
            subjectId: nextSubject.subject_id,
            operationType: 'ai_result_upgrade',
            runCode
        });

        return {
            execution_id: executionId,
            subject_id: nextSubject.subject_id,
            upgrade_state: 'awaiting_dispatch',
            ai_dispatch_state: 'awaiting_dispatch',
            target_generation: targetGeneration,
            current_unlocked_generation_before: toNumber(subject?.viewer_state?.current_unlocked_generation, 1),
            current_visible_result_id: null
        };
    },

    onActivate() {
        this._restorePersistedRuntimeState();

        const control = this._resolveControl();
        const shouldClearReentry = [
            control.quoteRunMode,
            control.runMode,
            control.assetResolutionMode
        ].some((mode) => mode !== 'success' && mode !== 'standard');

        if (shouldClearReentry) {
            clearResultReentryContext();
        }
    },

    handlers: {
        /**
         * T-U10: Upload 首屏参考目录
         */
        async 'GET /analysis/reference-catalogs/'(_payload, context) {
            await waitMockDelay(this._delayMultiplier() * 0.5);

            const requestedStatus = normalizeString(context?.params?.status || 'active') || 'active';
            const allItems = createReferenceCatalogItems();
            const items = requestedStatus === 'all'
                ? allItems
                : allItems.filter((item) => item.status === requestedStatus);

            return {
                items,
                total: items.length,
                policy_version: POLICY_VERSION
            };
        },

        /**
         * 02 契约补齐：Reference Detail
         */
        async 'GET /analysis/reference-catalogs/:referenceCode/'(_payload, context) {
            await waitMockDelay(this._delayMultiplier() * 0.4);

            const referenceCode = normalizeString(context?.params?.referenceCode);
            const item = createReferenceCatalogItems().find((entry) => entry.reference_code === referenceCode);
            if (!item) {
                throw buildMockError({
                    code: 'reference_not_found',
                    status: 404,
                    message: 'reference_not_found'
                });
            }

            return item;
        },

        /**
         * T-U10: capability-catalogs
         */
        async 'GET /analysis/capability-catalogs/'(_payload, _context) {
            await waitMockDelay(this._delayMultiplier() * 0.5);

            return clone(MOCK_CAPABILITY_SNAPSHOT);
        },

        /**
         * Payments 状态查询
         */
        async 'GET /payments/status/'(_payload, _context) {
            await waitMockDelay(this._delayMultiplier() * 0.3);

            return {
                user_id: 'mock_user_001',
                user_state: 'paid',
                entitlement: {
                    kind: 'subscription',
                    tier: 'standard',
                    entitlement_period: 'monthly'
                },
                current_cycle: {
                    start: 1711900800,
                    end: 1714492800
                },
                credits: {
                    trial: 5,
                    cycle: 100,
                    total: 105
                },
                tier: 'standard',
                policy_version: POLICY_VERSION
            };
        },

        /**
         * Quotes 总入口：run / capability_unlock / ai_result_upgrade
         */
        async 'POST /analysis/quotes/'(payload = {}) {
            await waitMockDelay(this._delayMultiplier());

            const operationType = normalizeString(payload.operation_type);

            if (operationType === 'run') {
                const originalImageHashId = normalizeString(payload.original_image_hash_id);
                const referenceCode = normalizeString(payload.reference_code);
                const runCode = normalizeString(payload.run_code) || DEFAULT_RUN_CODE;
                if (!originalImageHashId || !referenceCode || !runCode) {
                    this._buildRunError('invalid_request', 400, 'invalid_request');
                }

                const control = this._resolveControl();
                const mode = control.quoteRunMode;
                const pendingKey = `quote_run::${originalImageHashId}::${referenceCode}::${runCode}`;
                const shouldReturnPending = shouldTriggerPendingByMode({
                    mode,
                    tracker: this.state.quotePendingTracker,
                    key: pendingKey
                });
                if (shouldReturnPending) {
                    return this._buildRunQuoteResponse({
                        sufficientToContinue: false,
                        sufficientCredits: false,
                        reasonCode: 'payment_materialization_pending',
                        paymentPending: true,
                        nextAction: createRefreshPaymentsStatusNextAction()
                    });
                }

                if (mode === 'insufficient_credits') {
                    return this._buildRunQuoteResponse({
                        sufficientToContinue: false,
                        sufficientCredits: false,
                        reasonCode: 'insufficient_credits',
                        paymentPending: false,
                        nextAction: {
                            type: 'confirm_charge',
                            params: {
                                required_credits: 9
                            }
                        }
                    });
                }

                if (mode === 'history_quota_exceeded') {
                    return this._buildRunQuoteResponse({
                        sufficientToContinue: false,
                        sufficientCredits: true,
                        reasonCode: 'history_quota_exceeded',
                        paymentPending: false,
                        nextAction: {
                            type: 'confirm_charge',
                            params: {
                                message: 'History 配额已用完'
                            }
                        }
                    });
                }

                return this._buildRunQuoteResponse({
                    sufficientToContinue: true,
                    sufficientCredits: true,
                    reasonCode: null,
                    paymentPending: false,
                    nextAction: {
                        type: 'proceed'
                    }
                });
            }

            if (operationType === 'capability_unlock') {
                const subjectId = normalizeString(payload.subject_id);
                const capabilityCodes = normalizeCapabilityCodeSet(payload.capability_codes || []);
                if (!subjectId || capabilityCodes.length === 0) {
                    this._buildRunError('invalid_request', 400, 'invalid_request');
                }

                const subject = this._findSubjectInScenario(subjectId);
                if (!subject) {
                    this._buildRunError('subject_not_found', 404, 'subject_not_found');
                }

                const control = this._resolveControl();
                const mode = control.quoteUnlockMode;
                const pendingKey = `quote_unlock::${stringifyCapabilitySetKey(subjectId, capabilityCodes)}`;
                const shouldReturnPending = shouldTriggerPendingByMode({
                    mode,
                    tracker: this.state.quotePendingTracker,
                    key: pendingKey
                });
                if (shouldReturnPending) {
                    return this._buildUnlockQuoteResponse({
                        subjectId,
                        capabilityCodes,
                        sufficientToContinue: false,
                        sufficientCredits: false,
                        reasonCode: 'payment_materialization_pending',
                        paymentPending: true,
                        nextAction: createRefreshPaymentsStatusNextAction()
                    });
                }

                if (mode === 'insufficient_credits') {
                    return this._buildUnlockQuoteResponse({
                        subjectId,
                        capabilityCodes,
                        sufficientToContinue: false,
                        sufficientCredits: false,
                        reasonCode: 'insufficient_credits',
                        paymentPending: false,
                        nextAction: {
                            type: 'confirm_charge'
                        }
                    });
                }

                return this._buildUnlockQuoteResponse({
                    subjectId,
                    capabilityCodes,
                    sufficientToContinue: true,
                    sufficientCredits: true,
                    reasonCode: null,
                    paymentPending: false,
                    nextAction: {
                        type: 'confirm_unlock'
                    }
                });
            }

            if (operationType === 'ai_result_upgrade') {
                const subjectId = normalizeString(payload.subject_id);
                const runCode = normalizeString(payload.run_code) || DEFAULT_RUN_CODE;
                if (!subjectId) {
                    this._buildRunError('invalid_request', 400, 'invalid_request');
                }

                this._resolveSubjectForUpgrade(subjectId);
                const control = this._resolveControl();
                const mode = control.quoteUpgradeMode;

                const pendingKey = `quote_upgrade::${subjectId}::${runCode}`;
                const shouldReturnPending = shouldTriggerPendingByMode({
                    mode,
                    tracker: this.state.quotePendingTracker,
                    key: pendingKey
                });

                if (mode === 'paid_upgrade_not_available') {
                    this._buildRunError('paid_upgrade_not_available', 409, 'paid_upgrade_not_available');
                }

                if (shouldReturnPending) {
                    return this._buildUpgradeQuoteResponse({
                        subjectId,
                        runCode,
                        sufficientToContinue: false,
                        sufficientCredits: false,
                        reasonCode: 'payment_materialization_pending',
                        paymentPending: true,
                        nextAction: createRefreshPaymentsStatusNextAction()
                    });
                }

                if (mode === 'insufficient_credits') {
                    return this._buildUpgradeQuoteResponse({
                        subjectId,
                        runCode,
                        sufficientToContinue: false,
                        sufficientCredits: false,
                        reasonCode: 'insufficient_credits',
                        paymentPending: false,
                        nextAction: {
                            type: 'confirm_charge'
                        }
                    });
                }

                return this._buildUpgradeQuoteResponse({
                    subjectId,
                    runCode,
                    sufficientToContinue: true,
                    sufficientCredits: true,
                    reasonCode: null,
                    paymentPending: false,
                    nextAction: {
                        type: 'confirm_upgrade'
                    }
                });
            }

            return null;
        },

        /**
         * run-asset-resolutions
         */
        async 'POST /analysis/run-asset-resolutions/'(payload = {}) {
            await waitMockDelay(this._delayMultiplier() * 0.6);

            const originalImageHashId = normalizeString(payload.original_image_hash_id);
            const processedImageHashId = normalizeString(payload.processed_image_hash_id);
            if (!originalImageHashId) {
                this._buildRunError('invalid_request', 400, 'invalid_request');
            }

            const mode = this._resolveControl().assetResolutionMode;

            if (mode === 'processed_mismatch') {
                this._buildRunError('processed_mismatch', 409, 'processed_mismatch', {
                    upload_required: true,
                    required_upload_assets: ['processed']
                });
            }

            const originalUploadRequired = mode === 'original_upload_required' || mode === 'source_not_ready';
            const processedUploadRequired = mode === 'processed_upload_required';
            const sourceReady = mode !== 'source_not_ready';

            const aiEffectiveSourceKind = processedImageHashId && sourceReady
                ? 'processed'
                : (sourceReady ? 'original' : null);
            const aiEffectiveSourceHashId = aiEffectiveSourceKind === 'processed'
                ? processedImageHashId
                : (aiEffectiveSourceKind === 'original' ? originalImageHashId : null);

            return {
                original_image_resolution: {
                    hash_id: originalImageHashId,
                    image_record_exists: !originalUploadRequired,
                    object_storage_status: originalUploadRequired ? 'missing' : 'available',
                    relation_record_status: originalUploadRequired ? 'missing' : 'active',
                    upload_required: originalUploadRequired
                },
                processed_image_resolution: processedImageHashId
                    ? {
                        hash_id: processedImageHashId,
                        image_record_exists: !processedUploadRequired,
                        object_storage_status: processedUploadRequired ? 'missing' : 'available',
                        pair_binding_status: processedUploadRequired ? 'unbound' : 'matched',
                        upload_required: processedUploadRequired
                    }
                    : null,
                ai_source_resolution: {
                    ai_effective_source_kind: aiEffectiveSourceKind,
                    ai_effective_source_hash_id: aiEffectiveSourceHashId,
                    source_ready: sourceReady,
                    upload_required: !sourceReady
                }
            };
        },

        /**
         * runs
         */
        async 'POST /analysis/runs/'(payload = {}) {
            await waitMockDelay(this._delayMultiplier());

            const requestId = normalizeString(payload.request_id);
            const originalImageHashId = normalizeString(payload.original_image_hash_id);
            const referenceCode = normalizeString(payload.reference_code);
            const runCode = normalizeString(payload.run_code) || DEFAULT_RUN_CODE;

            if (!requestId || !originalImageHashId || !referenceCode || !runCode) {
                this._buildRunError('invalid_request', 400, 'invalid_request');
            }

            if (this.state.requestRunIndex.has(requestId)) {
                return clone(this.state.requestRunIndex.get(requestId));
            }

            const mode = this._resolveControl().runMode;
            const mutationPendingKey = this._buildMutationPendingKey('run', requestId);
            const shouldReturnPending = shouldTriggerPendingByMode({
                mode,
                tracker: this.state.mutationPendingTracker,
                key: mutationPendingKey
            });

            if (shouldReturnPending) {
                this._buildRunError('payment_materialization_pending', 409, 'payment_materialization_pending', buildPaymentMaterializationPendingExtra());
            }

            if (mode === 'insufficient_credits') {
                this._buildRunError('insufficient_credits', 402, 'insufficient_credits');
            }
            if (mode === 'history_quota_exceeded') {
                this._buildRunError('history_quota_exceeded', 409, 'history_quota_exceeded');
            }
            if (mode === 'image_missing') {
                this._buildRunError('image_missing', 404, 'image_missing', {
                    upload_required: true,
                    required_upload_assets: ['original']
                });
            }
            if (mode === 'processed_mismatch') {
                this._buildRunError('processed_mismatch', 409, 'processed_mismatch', {
                    upload_required: true,
                    required_upload_assets: ['processed']
                });
            }
            if (mode === 'rate_limited') {
                this._buildRunError('rate_limited', 429, 'rate_limited');
            }

            const executionId = `exec_${generateId().slice(0, 12)}`;
            const subjectId = `sub_${generateId().slice(0, 12)}`;
            const aiEffectiveSourceKind = normalizeString(payload.ai_effective_source_kind) || 'processed';
            const aiEffectiveSourceHashId = normalizeString(payload.ai_effective_source_hash_id)
                || normalizeString(payload.processed_image_hash_id)
                || (aiEffectiveSourceKind === 'original' ? originalImageHashId : `hash_processed_${generateId().slice(0, 8)}`);
            const aiDispatchState = this._resolveRunDispatchState(mode);

            const baseSubject = aiDispatchState === 'not_required'
                ? subjectAiCompleted({
                    subjectId,
                    executionId,
                    requestId,
                    originalHashId: originalImageHashId,
                    processedHashId: aiEffectiveSourceHashId,
                    referenceCode,
                    runCode,
                    operationType: 'run',
                    aiEffectiveSourceKind,
                    aiEffectiveSourceHashId,
                    analysisMarkdown: null
                })
                : standardSubjectReady({
                    subjectId,
                    executionId,
                    requestId,
                    originalHashId: originalImageHashId,
                    processedHashId: aiEffectiveSourceHashId,
                    referenceCode,
                    runCode,
                    operationType: 'run',
                    aiDispatchState: 'awaiting_dispatch',
                    aiEffectiveSourceKind,
                    aiEffectiveSourceHashId
                });

            if (aiDispatchState === 'not_required') {
                baseSubject.ai_state = {
                    ...(baseSubject.ai_state || {}),
                    ai_result_exists: Boolean(baseSubject.current_visible_result),
                    ai_dispatch_state: 'not_required',
                    dispatch_state: 'not_required'
                };
                baseSubject.pending_ai_execution = null;
            }

            this._setSubject(subjectId, baseSubject);
            this._bindExecution({
                executionId,
                subjectId,
                operationType: 'run',
                runCode
            });
            this.state.executedRuns.add(executionId);

            const response = withPolicyContext({
                execution_id: executionId,
                subject_id: subjectId,
                request_id: requestId,
                reference_code: referenceCode,
                ai_dispatch_state: aiDispatchState,
                ai_effective_source_kind: aiEffectiveSourceKind,
                ai_effective_source_hash_id: aiEffectiveSourceHashId,
                charged_total: 12,
                charged_items: createRunChargeItems(),
                granted_capabilities: createRunChargeItems().map((item) => item.capability_code),
                fulfillment_status: aiDispatchState,
                ai_result_id: null,
                current_visible_result_id: null,
                result_access_mode: null,
                upsell_hints: {
                    unlock_candidates: ['summary.ai_gaps_summary'],
                    tier_upgrade_candidates: ['pro'],
                    recommended_action: 'unlock'
                }
            });

            this.state.requestRunIndex.set(requestId, clone(response));
            return response;
        },

        /**
         * subjects（upload 场景只接管自身创建的 subject，未命中时返回 null 由下游场景兜底）
         */
        async 'GET /analysis/subjects/:id'(_payload, context) {
            await waitMockDelay(this._delayMultiplier() * 0.8);

            const subjectId = normalizeString(context?.params?.id);
            const subject = this._getSubject(subjectId);
            if (!subject) {
                return null;
            }
            return subject;
        },

        /**
         * ai-jobs 创建
         */
        async 'POST /analysis/ai-jobs/'(payload = {}, context = {}) {
            await waitMockDelay(this._delayMultiplier());

            const requestId = normalizeString(payload.request_id);
            const executionId = normalizeString(payload.execution_id);
            const toneContext = payload.tone_color_adjustment_context || null;

            console.log('[Mock] ai-jobs create request:', { requestId, executionId, toneContext: toneContext ? 'present' : 'missing' });

            if (!requestId || !executionId) {
                console.log('[Mock] ai-jobs create error: missing request_id or execution_id');
                this._buildRunError('invalid_request', 400, 'invalid_request');
            }

            if (!toneContext || !normalizeString(toneContext.schema_version)) {
                console.log('[Mock] ai-jobs create error: invalid tone_context');
                this._buildRunError('invalid_tone_context', 400, 'invalid_tone_context');
            }

            let binding = this._resolveExecutionBinding(executionId);

            // 如果没有binding，尝试从payload中创建临时binding（兼容真实接口创建的execution）
            if (!binding) {
                const subjectIdFromPayload = normalizeString(payload.subject_id);
                const operationTypeFromPayload = normalizeString(payload.operation_type) || 'run';
                const runCodeFromPayload = normalizeString(payload.run_code) || DEFAULT_RUN_CODE;

                if (subjectIdFromPayload) {
                    console.log('[Mock] ai-jobs creating temporary binding for real execution:', {
                        executionId,
                        subjectId: subjectIdFromPayload,
                        operationType: operationTypeFromPayload
                    });

                    // 创建临时binding
                    this._bindExecution({
                        executionId,
                        subjectId: subjectIdFromPayload,
                        operationType: operationTypeFromPayload,
                        runCode: runCodeFromPayload
                    });

                    // 重新获取binding
                    binding = this._resolveExecutionBinding(executionId);

                    // 如果subject不存在，创建一个占位subject
                    if (!this._findSubjectInScenario(subjectIdFromPayload)) {
                        const placeholderSubject = standardSubjectReady({
                            subjectId: subjectIdFromPayload,
                            originalHashId: `real_${generateId().slice(0, 8)}`,
                            processedHashId: `real_${generateId().slice(0, 8)}`,
                            referenceCode: 'reference_1',
                            runCode: runCodeFromPayload,
                            aiDispatchState: 'awaiting_dispatch'
                        });
                        // 添加pending execution
                        placeholderSubject.pending_ai_execution = {
                            execution_id: executionId,
                            operation_type: operationTypeFromPayload,
                            ai_dispatch_state: 'awaiting_dispatch',
                            requestable: true,
                            ai_effective_source_kind: 'processed'
                        };
                        this._setSubject(subjectIdFromPayload, placeholderSubject);
                        console.log('[Mock] ai-jobs created placeholder subject for real execution:', subjectIdFromPayload);
                    }
                }
            }

            if (!binding) {
                console.log('[Mock] ai-jobs create error: no execution binding for', executionId);
                this._buildRunError('ai_dispatch_not_required', 409, 'ai_dispatch_not_required');
            }

            console.log('[Mock] ai-jobs execution binding:', binding);

            const control = this._resolveControl();
            const createMode = control.aiJobCreateMode;

            console.log('[Mock] ai-jobs create mode:', createMode);

            if (createMode === 'invalid_tone_context') {
                this._buildRunError('invalid_tone_context', 400, 'invalid_tone_context');
            }
            if (createMode === 'ai_dispatch_not_required') {
                this._buildRunError('ai_dispatch_not_required', 409, 'ai_dispatch_not_required', {
                    next_action: createRefreshSubjectNextAction({ subjectId: binding.subject_id })
                });
            }
            if (createMode === 'ai_dispatch_input_conflict') {
                this._buildRunError('ai_dispatch_input_conflict', 409, 'ai_dispatch_input_conflict', {
                    next_action: createRestartAnalysisFlowNextAction({
                        subjectId: binding.subject_id,
                        executionId
                    })
                });
            }
            if (createMode === 'delivery_retryable') {
                const retryRequestId = `retry_${generateId().slice(0, 12)}`;
                this._buildRunError('delivery_init_failed_retryable', 503, 'delivery_init_failed_retryable', {
                    retry_request_id: retryRequestId,
                    next_action: {
                        ...createRetryCreateAiJobNextAction({
                            executionId,
                            retryRequestId
                        }),
                        reason: 'AI 服务暂时不可用，建议稍后重试'
                    }
                });
            }
            if (createMode === 'manual_recovery_required') {
                this._buildRunError('manual_recovery_required', 409, 'manual_recovery_required', {
                    next_action: createManualRecoveryNextAction({
                        subjectId: binding.subject_id,
                        executionId
                    })
                });
            }
            if (createMode === 'idempotency_conflict') {
                const retryRequestId = `retry_${generateId().slice(0, 12)}`;
                throw buildMockError({
                    code: 'idempotency_conflict',
                    status: 409,
                    message: '同一 execution_id 已绑定过其他 dispatch request_id',
                    responseData: {
                        reason_code: 'idempotency_conflict',
                        retry_request_id: retryRequestId,
                        next_action: createRetryCreateAiJobNextAction({
                            executionId,
                            retryRequestId
                        })
                    }
                });
            }
            if (createMode === 'idempotency_conflict_missing_retry_id') {
                throw buildMockError({
                    code: 'idempotency_conflict',
                    status: 409,
                    message: '同一 execution_id 已绑定过其他 dispatch request_id',
                    responseData: {
                        reason_code: 'idempotency_conflict',
                        next_action: createRetryCreateAiJobNextAction({ executionId })
                    }
                });
            }

            if (createMode === 'roll_forward_recovery_success') {
                const jobId = `job_roll_forward_recovery`;
                const aiResultId = `result_roll_forward_recovery`;
                this.state.aiJobs.set(jobId, {
                    job_id: jobId,
                    subject_id: binding.subject_id,
                    execution_id: executionId,
                    status: 'queued',
                    ai_dispatch_state: 'queued',
                    ai_result_id: aiResultId,
                    created_at: nowIso()
                });
                return {
                    status: 200,
                    data: {
                        execution_id: executionId,
                        subject_id: binding.subject_id,
                        job_id: jobId,
                        status: 'queued',
                        ai_dispatch_state: 'queued',
                        ai_result_id: aiResultId,
                        next_action: {
                            type: 'poll_ai_job',
                            method: 'GET',
                            endpoint: `/analysis/ai-jobs/${jobId}/`,
                            job_id: jobId
                        }
                    }
                };
            }

            if (createMode === 'ai_dispatch_input_conflict_full') {
                this._buildRunError('ai_dispatch_input_conflict', 409, 'ai_dispatch_input_conflict', {
                    requestable: false,
                    retryable: false,
                    failure_code: 'ai_dispatch_input_conflict',
                    recovery_mode: 'manual_recovery',
                    next_action: {
                        type: 'refresh_subject',
                        method: 'GET',
                        endpoint: `/analysis/subjects/${binding.subject_id}/`,
                        subject_id: binding.subject_id
                    }
                });
            }

            if (createMode === 'delivery_init_failed_retryable') {
                const retryRequestId = `retry_${generateId().slice(0, 12)}`;
                this._buildRunError('delivery_init_failed_retryable', 503, 'delivery_init_failed_retryable', {
                    requestable: true,
                    retryable: true,
                    retry_with_same_request_id: true,
                    failure_code: 'delivery_init_failed_retryable',
                    recovery_mode: 'retry_same_ai_result',
                    retry_request_id: retryRequestId,
                    next_action: {
                        type: 'retry_create_ai_job',
                        method: 'POST',
                        endpoint: '/analysis/ai-jobs/',
                        execution_id: executionId,
                        retry_request_id: retryRequestId
                    }
                });
            }

            const replayKey = `${executionId}::${requestId}`;
            const replayJobId = this.state.aiReplayIndex.get(replayKey);
            if (replayJobId && this.state.aiJobs.has(replayJobId)) {
                console.log('[Mock] ai-jobs replay hit:', replayJobId);
                const response = this._buildAiJobResponse(this.state.aiJobs.get(replayJobId));
                return context?.includeMeta === true
                    ? buildMockHttpResponse(response, { status: 200 })
                    : response;
            }

            const existingPendingJobId = this.state.pendingJobByExecution.get(executionId);
            if (existingPendingJobId && this.state.aiJobs.has(existingPendingJobId)) {
                const existingJob = this.state.aiJobs.get(existingPendingJobId);
                if (existingJob.status === 'queued' || existingJob.status === 'running') {
                    console.log('[Mock] ai-jobs existing pending hit:', existingPendingJobId);
                    this.state.aiReplayIndex.set(replayKey, existingPendingJobId);
                    const response = this._buildAiJobResponse(existingJob);
                    return context?.includeMeta === true
                        ? buildMockHttpResponse(response, { status: 200 })
                        : response;
                }
            }

            const subject = this._findSubjectInScenario(binding.subject_id);
            if (!subject) {
                console.log('[Mock] ai-jobs create error: subject not found', binding.subject_id);
                this._buildRunError('subject_not_found', 404, 'subject_not_found');
            }

            const jobId = `job_${generateId().slice(0, 12)}`;
            const outcomeMode = control.aiJobOutcomeMode;
            const retryRequestId = `retry_${generateId().slice(0, 12)}`;
            const missingPollJobIdInNextAction = createMode === 'success_missing_poll_job_id';

            console.log('[Mock] ai-jobs creating new job:', { jobId, executionId, subjectId: binding.subject_id, outcomeMode });

            const job = {
                job_id: jobId,
                execution_id: executionId,
                subject_id: binding.subject_id,
                operation_type: binding.operation_type,
                request_id: requestId,
                retry_request_id: retryRequestId,
                status: 'queued',
                progress_percent: 5,
                poll_count: 0,
                retryable: false,
                retry_with_same_request_id: false,
                next_action: createPollAiJobNextAction({
                    jobId,
                    omitJobId: missingPollJobIdInNextAction
                }),
                outcome_mode: outcomeMode,
                missing_poll_job_id_in_next_action: missingPollJobIdInNextAction,
                tone_context_size: JSON.stringify(toneContext).length
            };

            this.state.aiJobs.set(jobId, job);
            this.state.aiReplayIndex.set(replayKey, jobId);
            this.state.pendingJobByExecution.set(executionId, jobId);

            console.log('[Mock] ai-jobs job stored, updating subject ai_state to running');

            const nextSubject = cloneSubjectPayload(subject);
            nextSubject.ai_state = {
                ...(nextSubject.ai_state || {}),
                ai_result_exists: Boolean(nextSubject.current_visible_result),
                ai_dispatch_state: 'running',
                dispatch_state: 'running'
            };
            nextSubject.pending_ai_execution = {
                execution_id: executionId,
                operation_type: binding.operation_type,
                ai_dispatch_state: 'running',
                target_generation: binding.operation_type === 'ai_result_upgrade'
                    ? toNumber(nextSubject?.upgrade_offer?.target_generation, 2)
                    : null,
                requestable: true,
                job_id: jobId,
                status: 'queued',
                request_id: requestId,
                retryable: false,
                retry_with_same_request_id: false,
                retry_request_id: retryRequestId,
                ai_effective_source_kind: nextSubject?.pending_ai_execution?.ai_effective_source_kind || 'processed',
                ai_effective_source_hash_id: nextSubject?.pending_ai_execution?.ai_effective_source_hash_id || nextSubject?.subject_context?.processed_image_hash || null,
                dispatch_payload: {
                    execution_id: executionId,
                    request_id: requestId,
                    tone_color_adjustment_context: toneContext
                },
                next_action: job.next_action
            };
            this._setSubject(nextSubject.subject_id, nextSubject);
            this._persistRuntimeState();

            console.log('[Mock] ai-jobs create success:', { jobId, status: 'queued', pollUrl: `/analysis/ai-jobs/${jobId}/` });

            const response = this._buildAiJobResponse(job);
            return context?.includeMeta === true
                ? buildMockHttpResponse(response, { status: 202 })
                : response;
        },

        /**
         * ai-jobs 轮询
         */
        async 'GET /analysis/ai-jobs/:id'(_payload, context) {
            await waitMockDelay(this._delayMultiplier() * 0.6);

            const jobId = normalizeString(context?.params?.id);
            console.log('[Mock] ai-jobs poll request:', { jobId });

            if (!jobId || !this.state.aiJobs.has(jobId)) {
                console.log('[Mock] ai-jobs poll error: job not found', jobId);
                this._buildRunError('ai_job_not_found', 404, 'ai_job_not_found');
            }

            const job = this.state.aiJobs.get(jobId);
            const binding = this._resolveExecutionBinding(job.execution_id);
            if (!binding) {
                console.log('[Mock] ai-jobs poll error: no execution binding', job.execution_id);
                this._buildRunError('ai_dispatch_not_required', 409, 'ai_dispatch_not_required');
            }

            console.log('[Mock] ai-jobs current state:', { jobId, status: job.status, progress: job.progress_percent, pollCount: job.poll_count });

            this._advanceAiJob(job);

            console.log('[Mock] ai-jobs advanced to:', { jobId, status: job.status, progress: job.progress_percent });

            if (job.status === 'completed') {
                console.log('[Mock] ai-jobs completed, updating subject');
                this._handleAiJobCompletionToSubject({
                    ...binding,
                    execution_id: job.execution_id
                }, job);
                this.state.pendingJobByExecution.delete(job.execution_id);
            } else if (job.status === 'failed') {
                console.log('[Mock] ai-jobs failed, updating subject');
                this._handleAiJobFailureToSubject({
                    ...binding,
                    execution_id: job.execution_id
                }, job);
                this.state.pendingJobByExecution.delete(job.execution_id);
            } else {
                const subject = this._findSubjectInScenario(binding.subject_id);
                if (subject) {
                    const nextSubject = cloneSubjectPayload(subject);
                    nextSubject.ai_state = {
                        ...(nextSubject.ai_state || {}),
                        ai_dispatch_state: 'running',
                        dispatch_state: 'running'
                    };
                    nextSubject.pending_ai_execution = {
                        ...(nextSubject.pending_ai_execution || {}),
                        execution_id: job.execution_id,
                        operation_type: binding.operation_type,
                        ai_dispatch_state: 'running',
                        requestable: true,
                        job_id: job.job_id,
                        status: job.status,
                        request_id: job.request_id,
                        retryable: false,
                        retry_with_same_request_id: false,
                        retry_request_id: job.retry_request_id,
                        next_action: job.next_action
                    };
                    this._setSubject(nextSubject.subject_id, nextSubject);
                }
            }

            this._persistRuntimeState();

            return this._buildAiJobResponse(job);
        },

        /**
         * capability-unlocks
         */
        async 'POST /analysis/capability-unlocks/'(payload = {}) {
            await waitMockDelay(this._delayMultiplier());

            const requestId = normalizeString(payload.request_id);
            const subjectId = normalizeString(payload.subject_id);
            const capabilityCodes = normalizeCapabilityCodeSet(payload.capability_codes || []);

            if (!requestId || !subjectId || capabilityCodes.length === 0) {
                this._buildRunError('invalid_request', 400, 'invalid_request');
            }

            if (Object.prototype.hasOwnProperty.call(payload, 'confirm_capability_codes')) {
                this._buildRunError(
                    'confirm_capability_codes: Extra inputs are not permitted',
                    400,
                    'invalid_request',
                    {
                        details: [
                            {
                                input: payload.confirm_capability_codes,
                                loc: ['confirm_capability_codes'],
                                msg: 'Extra inputs are not permitted'
                            }
                        ]
                    }
                );
            }

            if (this.state.requestUnlockIndex.has(requestId)) {
                return clone(this.state.requestUnlockIndex.get(requestId));
            }

            const subject = this._findSubjectInScenario(subjectId);
            if (!subject) {
                this._buildRunError('subject_not_found', 404, 'subject_not_found');
            }

            const mode = this._resolveControl().capabilityUnlockMode;
            const mutationPendingKey = this._buildMutationPendingKey('capability_unlock', requestId);
            const shouldReturnPending = shouldTriggerPendingByMode({
                mode,
                tracker: this.state.mutationPendingTracker,
                key: mutationPendingKey
            });

            if (shouldReturnPending) {
                this._buildRunError('payment_materialization_pending', 409, 'payment_materialization_pending', buildPaymentMaterializationPendingExtra());
            }
            if (mode === 'insufficient_credits') {
                this._buildRunError('insufficient_credits', 402, 'insufficient_credits');
            }
            if (mode === 'rate_limited') {
                this._buildRunError('rate_limited', 429, 'rate_limited');
            }

            if (mode === 'confirm_mismatch') {
                this._buildRunError('confirm_capabilities_mismatch', 400, 'confirm_capabilities_mismatch');
            }

            const updatedSubject = applyCapabilityUnlockToSubject(subject, capabilityCodes);
            this._setSubject(subjectId, updatedSubject);

            const chargedItems = capabilityCodes.map((capabilityCode) => {
                const unlockableEntry = findUnlockableCapabilityEntry(subject, capabilityCode);
                return {
                    capability_code: capabilityCode,
                    credits: extractCapabilityCredits(unlockableEntry)
                };
            });

            const response = withPolicyContext({
                consumption_id: `cons_${generateId().slice(0, 12)}`,
                subject_id: subjectId,
                request_id: requestId,
                charged_total: chargedItems.reduce((acc, item) => acc + toNumber(item.credits, 0), 0),
                charged_items: chargedItems,
                granted_capabilities: capabilityCodes,
                fulfillment_status: 'not_required',
                upsell_hints: {
                    unlock_candidates: [],
                    tier_upgrade_candidates: ['pro'],
                    recommended_action: 'none'
                }
            });

            this.state.requestUnlockIndex.set(requestId, clone(response));
            return response;
        },

        /**
         * ai-result-upgrades
         */
        async 'POST /analysis/ai-result-upgrades/'(payload = {}) {
            await waitMockDelay(this._delayMultiplier());

            const requestId = normalizeString(payload.request_id);
            const subjectId = normalizeString(payload.subject_id);
            const runCode = normalizeString(payload.run_code) || DEFAULT_RUN_CODE;

            if (!requestId || !subjectId) {
                this._buildRunError('invalid_request', 400, 'invalid_request');
            }

            if (this.state.requestUpgradeIndex.has(requestId)) {
                return clone(this.state.requestUpgradeIndex.get(requestId));
            }

            const subject = this._resolveSubjectForUpgrade(subjectId);
            const mode = this._resolveControl().upgradeMode;
            const mutationPendingKey = this._buildMutationPendingKey('ai_result_upgrade', requestId);
            const shouldReturnPending = shouldTriggerPendingByMode({
                mode,
                tracker: this.state.mutationPendingTracker,
                key: mutationPendingKey
            });

            if (mode === 'paid_upgrade_not_available') {
                this._buildRunError('paid_upgrade_not_available', 409, 'paid_upgrade_not_available');
            }
            if (shouldReturnPending) {
                this._buildRunError('payment_materialization_pending', 409, 'payment_materialization_pending', buildPaymentMaterializationPendingExtra());
            }
            if (mode === 'insufficient_credits') {
                this._buildRunError('insufficient_credits', 402, 'insufficient_credits');
            }
            if (mode === 'rate_limited') {
                this._buildRunError('rate_limited', 429, 'rate_limited');
            }

            const upgradeResponse = mode === 'awaiting_dispatch'
                ? this._createUpgradeExecution({
                    subject,
                    requestId,
                    runCode,
                    awaitingDispatch: true
                })
                : this._createUpgradeExecution({
                    subject,
                    requestId,
                    runCode,
                    awaitingDispatch: false
                });

            const response = withPolicyContext(upgradeResponse);
            this.state.requestUpgradeIndex.set(requestId, clone(response));
            return response;
        }
    },

    reset() {
        this.state.executedRuns.clear();
        this.state.createdSubjects.clear();
        this.state.aiJobs.clear();
        this.state.executionIndex.clear();
        this.state.requestRunIndex.clear();
        this.state.requestUnlockIndex.clear();
        this.state.requestUpgradeIndex.clear();
        this.state.quotePendingTracker.clear();
        this.state.mutationPendingTracker.clear();
        this.state.aiReplayIndex.clear();
        this.state.pendingJobByExecution.clear();
        this.state.control = {
            ...DEFAULT_CONTROL
        };
        clearPersistedScenarioState();
    },

    getSubject(subjectId) {
        return this._getSubject(subjectId);
    },

    setSubject(subjectId, data) {
        this._setSubject(subjectId, data);
    }
};
